import type { ReactNode } from "react";

import { useAuth } from "./AuthContext";
import { EvaluationJobProvider } from "./EvaluationJobContext";
import { FileEvaluationProvider } from "./FileEvaluationContext";
import { PublishProvider } from "./PublishContext";

/**
 * All three Context Console request lifecycles, composed into one wrapper.
 *
 * Kept as its own component rather than three separate `<Provider>` tags
 * inline in App.tsx: three open tags with no visible nesting relative to
 * their closing tags 80 lines later (all sitting at the same indent as their
 * siblings) makes the actual JSX structure unreadable at a glance. One tag
 * here, one there.
 *
 * All three providers get the signed-in user's identity: none is otherwise
 * aware of *who* it's tracking a run/evaluation/publish for, so on a shared machine
 * a same-tab account switch (Firebase fires null then a new uid, no reload)
 * would otherwise leave the previous user's in-memory `run`/`evaluation`
 * state — including per-question answers and the picked `File`s — sitting
 * there for whoever's now looking at the page. Each provider reacts to its
 * `identityKey` changing by clearing itself (see EvaluationJobContext's
 * identity-change effect for why this is a prop + effect, not a React `key`
 * remount — a remount race can lose to the sessionStorage-persisted half of
 * this same problem). `"anon"` is the shared sentinel for "auth hasn't
 * resolved yet" — every provider treats the *first* resolution away from it
 * (e.g. right after a page refresh, restoring the same user) as adopting an
 * identity, not switching away from one, so a legitimately resumed run
 * survives a refresh same as before.
 */
export const ContextConsoleProviders = ({ children }: { children: ReactNode }) => {
  const { userDetails } = useAuth();
  const identityKey = userDetails?.userId ?? "anon";

  return (
    <EvaluationJobProvider identityKey={identityKey}>
      <FileEvaluationProvider identityKey={identityKey}>
        <PublishProvider identityKey={identityKey}>{children}</PublishProvider>
      </FileEvaluationProvider>
    </EvaluationJobProvider>
  );
};
